import * as xlsx from "xlsx";
import { useWebWorkerFn } from "@vueuse/core";

export type XLSXConfigPage = {
  name: string;
  headers: {
    label: string;
    /** Excel cell width */
    width: number;
  }[];
  data: {
    value: string | number | Date;
  }[][];
};

type XLSXConfig = {
  fileName: string;
  title: string;
  author?: string;
  createdAt?: Date;
  pages: XLSXConfigPage[];
  document?: typeof document;
  window?: typeof window;
};

const EXCEL_WORKSHEET_NAME_MAX_LENGTH = 31;
const WORKSHEET_NAME_CHARACTER_WHITELIST = /[^\p{L}\p{M}\p{N} ._()&-]/gu;

export function sanitizeWorksheetName(name: string, usedNames: ReadonlySet<string> = new Set()) {
  const sanitized = name.replace(WORKSHEET_NAME_CHARACTER_WHITELIST, "_").trim() || "Sheet";
  const baseName = sanitized.slice(0, EXCEL_WORKSHEET_NAME_MAX_LENGTH);
  const normalizedUsedNames = new Set(
    [...usedNames].map((usedName) => usedName.toLocaleLowerCase()),
  );
  let worksheetName = baseName;
  let duplicateIndex = 2;

  while (normalizedUsedNames.has(worksheetName.toLocaleLowerCase())) {
    const suffix = `_${duplicateIndex}`;
    worksheetName = `${baseName.slice(0, EXCEL_WORKSHEET_NAME_MAX_LENGTH - suffix.length)}${suffix}`;
    duplicateIndex += 1;
  }

  return worksheetName;
}

/**
 * @author Lucas Wassenhoven
 * @description This function is used to export data to an Excel file.
 * @example
 * useXLSX({
 *    fileName: "MyExcelFile",
 *    title: "My Excel File",
 *    pages: [
 *     {
 *      name: "Sheet1",
 *      headers: [
 *        { label: "Name", width: 20 },
 *        { label: "Age", width: 10 },
 *        { label: "Date of Birth", width: 20 },
 *      ],
 *      data: [
 *        [
 *          { value: "John Doe" },
 *          { value: 30 },
 *          { value: new Date("1990-01-01") },
 *       ],
 *     ],
 *    },
 *  ],
 * });
 * @param {XLSXConfig} config - The configuration object for the Excel file.
 * @returns {void}
 */
export function useXLSX(_config: XLSXConfig, async = false) {
  const config = {
    author: "Frisch Solutions GmbH",
    createdAt: new Date(),
    window,
    document,
    ..._config,
  };
  const book = xlsx.utils.book_new();

  book.Props = {
    Title: config.title,
    Author: config.author,
    CreatedDate: config.createdAt,
  };

  const worksheetNames = new Set<string>();
  config.pages.forEach((page) => {
    const worksheetName = sanitizeWorksheetName(page.name, worksheetNames);
    worksheetNames.add(worksheetName);
    book.SheetNames.push(worksheetName);
    const sheet = xlsx.utils.json_to_sheet(
      [
        page.headers.map((header) => header.label),
        ...page.data.map((row) =>
          row.map((cell) =>
            typeof cell.value === "string" ? cell.value.substring(0, 32000) : cell.value,
          ),
        ),
      ],
      { skipHeader: true, dateNF: "yyyy-mm-dd;@", cellDates: true },
    );
    sheet["!cols"] = page.headers.map((header) => ({ wch: header.width }));
    book.Sheets[worksheetName] = sheet;
  });

  let blob: Blob | null = null;
  /**
   * this is blocking, i would love to move it to a worker!
   */
  if (async === false) {
    const buffer = xlsx.write(book, { bookType: "xlsx", type: "buffer" });
    blob = new Blob([buffer], { type: "application/octet-stream" });
  }
  return {
    downloadAsync: async () => {
      const { workerFn } = useWebWorkerFn(
        // @ts-expect-error this is available in the worker via the CDN
        (currentBook) => XLSX.write(currentBook, { bookType: "xlsx", type: "buffer" }),
        {
          timeout: 50000,
          dependencies: ["https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"],
        },
      );
      blob = await workerFn(book);
      if (blob == null) {
        throw new Error("Error creating the Excel file");
      }
      const a = config.document.createElement("a");
      a.style.display = "none";
      config.document.body.appendChild(a);
      a.href = config.window.URL.createObjectURL(
        new File([blob], `${config.fileName}.xlsx`, {
          type: "application/octet-stream",
        }),
      );
      a.setAttribute("download", `${config.fileName}.xlsx`);
      a.click();
      config.window.URL.revokeObjectURL(a.href);
      config.document.body.removeChild(a);
    },
    exportAsync: async () => {
      const { workerFn } = useWebWorkerFn(
        // @ts-expect-error this is available in the worker via the CDN
        (currentBook) => XLSX.write(currentBook, { bookType: "xlsx", type: "buffer" }),
        {
          timeout: 50000,
          dependencies: ["https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"],
        },
      );
      blob = await workerFn(book);
      if (blob == null) {
        throw new Error("Error creating the Excel file");
      }
      return new File([blob], `${config.fileName}.xlsx`, {
        type: "application/octet-stream",
      });
    },
    export: () => {
      if (async === true) {
        throw new Error("You are using this with async, use exportAsync instead!");
      }
      if (blob == null) {
        throw new Error("Error creating the Excel file");
      }
      return new File([blob], `${config.fileName}.xlsx`, {
        type: "application/octet-stream",
      });
    },
    download: () => {
      if (async === true) {
        throw new Error("You are using this with async, use downloadAsync instead!");
      }
      if (blob == null) {
        throw new Error("Error creating the Excel file");
      }
      const a = config.document.createElement("a");
      a.style.display = "none";
      config.document.body.appendChild(a);
      a.href = config.window.URL.createObjectURL(blob);
      a.setAttribute("download", `${config.fileName}.xlsx`);
      a.click();
      config.window.URL.revokeObjectURL(a.href);
      config.document.body.removeChild(a);
    },
  };
}
